import { isPublicMarketingPath } from "@/lib/marketing/nav-links";
import { isExploreNavPath } from "@/lib/platform/explore-nav";
import { isAuthenticatedPricingPath } from "@/lib/platform/pricing-destination";

/**
 * Authenticated monetization lives under `/billing/*` (see pricing-destination).
 * Public `/pricing` is guest-only; middleware redirects signed-in users away.
 */
const HYBRID_MONETIZATION_PREFIXES = ["/billing"] as const;

const APP_PRODUCT_PREFIXES = [
  "/home",
  "/notebook",
  "/library",
  "/settings",
  "/about",
  "/contact",
] as const;

/** Legal pages in app shell for signed-in users (footer only, not sidebar). */
const APP_LEGAL_PATHS = ["/privacy", "/terms"] as const;

export function isAppLegalPath(pathname: string): boolean {
  return APP_LEGAL_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/** Marketing / legal / support surfaces (top marketing nav, footer for all visitors). */
export function isMarketingChromePath(pathname: string): boolean {
  return isPublicMarketingPath(pathname);
}

export function isHybridMonetizationPath(pathname: string): boolean {
  if (isAuthenticatedPricingPath(pathname)) return true;
  return HYBRID_MONETIZATION_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function isAppProductPath(pathname: string): boolean {
  return APP_PRODUCT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Whether the shared marketing footer should render.
 *
 * - Guests: marketing/legal surfaces.
 * - Signed-in: Explore + billing app-shell pages (About, Contact, Pricing links).
 */
function isPublishedSurfacePath(pathname: string): boolean {
  return (
    pathname === "/billing/published" ||
    pathname.startsWith("/billing/published/") ||
    pathname.startsWith("/p/")
  );
}

export function shouldShowMarketingFooter(
  pathname: string,
  isAuthenticated: boolean,
): boolean {
  if (isPublishedSurfacePath(pathname)) return false;

  if (isAuthenticated) {
    return (
      isExploreNavPath(pathname) ||
      isHybridMonetizationPath(pathname) ||
      isAppLegalPath(pathname)
    );
  }
  return isMarketingChromePath(pathname);
}

/** Authenticated marketing top nav is retired — workspace sidebar is canonical. */
export function shouldUseAuthenticatedMarketingNav(_pathname: string): boolean {
  return false;
}

/** Surfaces that use the workspace sidebar when signed in (no marketing navbar). */
export function shouldUseAppShellForAuthenticated(
  pathname: string,
): boolean {
  return (
    isAppProductPath(pathname) ||
    isHybridMonetizationPath(pathname) ||
    isExploreNavPath(pathname) ||
    isAppLegalPath(pathname)
  );
}
